import React, { useEffect, useState, useContext } from "react";
import MenuButton from "../MenuButton/MenuButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import CurrencyFormat from "react-currency-format";

// Validation schema
const charitySchema = Yup.object().shape({
  branch_id: Yup.string().required("Branch is required"),
  date: Yup.date().required("Date is required"),
  type: Yup.string().required("Transaction type is required"),
  person: Yup.string().when('type', {
    is: (val) => val !== 'Transfer',
    then: () => Yup.string().required("Person is required"),
    otherwise: () => Yup.string().nullable(),
  }),
  from_person: Yup.string().when('type', {
    is: 'Transfer',
    then: () => Yup.string().required("Transfer from is required"),
    otherwise: () => Yup.string().nullable(),
  }),
  to_person: Yup.string().when('type', {
    is: 'Transfer',
    then: () => Yup.string().required("Transfer to is required"),
    otherwise: () => Yup.string().nullable(),
  }),
  amount: Yup.number()
    .required("Amount is required")
    .min(1, "Amount must be greater than 0"),
  notes: Yup.string(),
});

export default function AddCharity() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [myBranches, setMyBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balances, setBalances] = useState({
    f_cash_balance: 0,
    w_cash_balance: 0,
    total_charity: 0,
  });
  const [updatedBalances, setUpdatedBalances] = useState({
    f_cash_balance: 0,
    w_cash_balance: 0,
    total_charity: 0,
  });
  const [charityAmounts, setCharityAmounts] = useState({
    total_charity: 0,
  });

  const date = new Date();
  const defaultValue = date.toLocaleDateString("en-CA");

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await UserServices.branchName();
      const res = response.data.data;
      const branches = res.map((b) => ({ id: b.id, name: b.name }));
      setMyBranches([{ id: "", name: "Select Branch" }, ...branches]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch branches", { autoClose: 3000 });
    }
  };

  // Fetch "Today's Charity" (2.5% of sales) exactly like Cash In Hand form
  const fetchCharityAmounts = async (branchId, selectedDate) => {
    if (!branchId || !selectedDate) return;

    try {
      const response = await UserServices.getDetailSaleReport(branchId, {
        sdate: selectedDate,
        edate: selectedDate,
        report: "",
        revenueCat: "",
        package_type: "",
      });

      if (response?.data?.data?.detail) {
        const detail = response.data.data.detail;
        const totalNetPrice = detail.reduce((sum, item) => sum + (item?.net_price || 0), 0);
        const charityAmount = Math.round(totalNetPrice * 0.025);
        setCharityAmounts({ total_charity: charityAmount });
      } else {
        setCharityAmounts({ total_charity: 0 });
      }
    } catch (error) {
      console.error("Error fetching charity amounts:", error);
      setCharityAmounts({ total_charity: 0 });
    }
  };

  // Fetch current balances
  const fetchCurrentBalances = async (branchId, selectedDate) => {
    if (!branchId || !selectedDate) return;
    try {
      const response = await UserServices.getCharityCurrentBalance(
        branchId,
        selectedDate
      );
      if (response.data.success) {
        const data = response.data;
        const f = Number(data.f_cash_balance) || 0;
        const w = Number(data.w_cash_balance) || 0;
        const t = Number(data.total_charity) || (f + w);
        setBalances({
          f_cash_balance: f,
          w_cash_balance: w,
          total_charity: t,
        });
        setUpdatedBalances({
          f_cash_balance: f,
          w_cash_balance: w,
          total_charity: t,
        });
      }
    } catch (err) {
      console.error("Error fetching balances:", err);
      setBalances({
        f_cash_balance: 0,
        w_cash_balance: 0,
        total_charity: 0,
      });
      setUpdatedBalances({
        f_cash_balance: 0,
        w_cash_balance: 0,
        total_charity: 0,
      });
    }
  };

  // Update balances in real-time
  const updateBalancesRealTime = (type, person, amount, fromPerson, toPerson) => {
    let newFBalance = Number(balances.f_cash_balance) || 0;
    let newWBalance = Number(balances.w_cash_balance) || 0;
    const parsedAmount = Number(amount);

    if (!amount || isNaN(parsedAmount)) {
      setUpdatedBalances({
        f_cash_balance: balances.f_cash_balance,
        w_cash_balance: balances.w_cash_balance,
        total_charity: balances.total_charity,
      });
      return;
    }

    if (type === "Transfer") {
      // Transfer between persons - debit from one, credit to other
      if (!fromPerson || !toPerson) {
        setUpdatedBalances({
          f_cash_balance: balances.f_cash_balance,
          w_cash_balance: balances.w_cash_balance,
          total_charity: balances.total_charity,
        });
        return;
      }
      
      if (fromPerson === "Faisal" && toPerson === "Waqas") {
        newFBalance -= parsedAmount;
        newWBalance += parsedAmount;
      } else if (fromPerson === "Waqas" && toPerson === "Faisal") {
        newWBalance -= parsedAmount;
        newFBalance += parsedAmount;
      }
    } else if (type === "Credit") {
      // External credit - add money from outside
      if (person === "Faisal") {
        newFBalance += parsedAmount;
      } else if (person === "Waqas") {
        newWBalance += parsedAmount;
      }
    } else if (type === "Debit") {
      // External debit - remove money to outside
      if (person === "Faisal") {
        newFBalance -= parsedAmount;
      } else if (person === "Waqas") {
        newWBalance -= parsedAmount;
      }
    }

    const fNum = Number(newFBalance) || 0;
    const wNum = Number(newWBalance) || 0;
    setUpdatedBalances({
      f_cash_balance: fNum,
      w_cash_balance: wNum,
      total_charity: fNum + wNum,
    });
  };

  useEffect(() => {
    fetchBranches();
    if (branch_id) {
      fetchCurrentBalances(branch_id, defaultValue);
      fetchCharityAmounts(branch_id, defaultValue);
    }
  }, [branch_id]);

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange: formikHandleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      branch_id: branch_id || "",
      date: defaultValue,
      type: "",
      person: "",
      from_person: "",
      to_person: "",
      amount: "",
      notes: "",
    },
    validationSchema: charitySchema,
    onSubmit: async (values, action) => {
      const confirmed = window.confirm(
        "Do you want to save this charity transaction?"
      );
      if (!confirmed) return;

      setLoading(true);
      
      // Prepare data based on transaction type
      const submitData = {
        branch_id: values.branch_id,
        date: values.date,
        type: values.type,
        amount: values.amount,
        notes: values.notes,
      };

      // Add person fields based on transaction type
      if (values.type === "Transfer") {
        submitData.from_person = values.from_person;
        submitData.to_person = values.to_person;
      } else {
        submitData.person = values.person;
      }

      console.log('Submitting charity data:', submitData);
      try {
        const response = await UserServices.addCharity(submitData);
        console.log('Response:', response);
        if (response.status === 201) {
          toast.success("Charity Transaction Added Successfully!", {
            autoClose: 3000,
          });
          action.resetForm();
          fetchCurrentBalances(values.branch_id, values.date);
        } else {
          toast.error("Failed to add transaction", { autoClose: 3000 });
        }
      } catch (err) {
        console.error('Full error:', err);
        console.error('Error response:', err.response);
        console.error('Error data:', err.response?.data);
        toast.error(
          err.response?.data?.message || err.response?.data?.error || "Failed to add transaction",
          { autoClose: 3000 }
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const handleChange = (e) => {
    formikHandleChange(e);
    const { name, value } = e.target;

    // Update balances in real-time when amount, type, or person changes
    const updatedValues = { ...values, [name]: value };

    if (name === "branch_id" || name === "date") {
      fetchCurrentBalances(
        name === "branch_id" ? value : values.branch_id,
        name === "date" ? value : values.date
      );
      fetchCharityAmounts(
        name === "branch_id" ? value : values.branch_id,
        name === "date" ? value : values.date
      );
    } else {
      updateBalancesRealTime(
        updatedValues.type,
        updatedValues.person,
        updatedValues.amount,
        updatedValues.from_person,
        updatedValues.to_person
      );
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <div className="row">
                <div className="col-10">
                  <h5 className="mb-0 font-20">Add Charity Transaction</h5>
                </div>
                <div className="col-md-2 d-flex justify-content-end">
                  <Link
                    to="/view-charity"
                    className="btn btn-primary btn-sm"
                  >
                    View Charity Ledger
                  </Link>
                </div>
              </div>
            </div>
            <div className="card-body">
              <p>
                <i className="bi bi-exclamation-lg text-danger" />
                The Fields With <strong className="text-danger">*</strong> Must
                Be Filled.
              </p>

              {/* Charity Amount from Cash In Hand System */}
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="alert alert-info d-flex align-items-center" role="alert">
                    <i className="bi bi-heart-fill me-3 fs-4"></i>
                    <div className="flex-grow-1">
                      <strong className="fs-5">Today's Charity (2.5% of Sales):</strong>
                      <span className="ms-3 fs-4">
                        <CurrencyFormat
                          value={charityAmounts.total_charity}
                          displayType="text"
                          thousandSeparator
                          prefix="Rs "
                          suffix="/-"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Balances Display */}
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="mb-3">Current Balances (Till Selected Date):</h6>
                      <div className="row">
                        <div className="col-md-4">
                          <strong>Faisal Balance:</strong>{" "}
                          <CurrencyFormat
                            value={balances.f_cash_balance}
                            displayType="text"
                            thousandSeparator
                            prefix="Rs "
                            suffix="/-"
                          />
                        </div>
                        <div className="col-md-4">
                          <strong>Sir Waqas Balance:</strong>{" "}
                          <CurrencyFormat
                            value={balances.w_cash_balance}
                            displayType="text"
                            thousandSeparator
                            prefix="Rs "
                            suffix="/-"
                          />
                        </div>
                        <div className="col-md-4">
                          <strong>Total Charity:</strong>{" "}
                          <CurrencyFormat
                            value={balances.total_charity}
                            displayType="text"
                            thousandSeparator
                            prefix="Rs "
                            suffix="/-"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <form className="row g-3" onSubmit={handleSubmit}>
                <div className="col-md-6">
                  <label className="form-label">
                    Branch Name <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="branch_id"
                    value={values.branch_id}
                    disabled={!!branch_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {myBranches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  {errors.branch_id && touched.branch_id && (
                    <p className="text-danger">{errors.branch_id}</p>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={values.date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.date && touched.date && (
                    <p className="text-danger">{errors.date}</p>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Transaction Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="type"
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select Type</option>
                    <option value="Credit">Credit (Add Money from Outside)</option>
                    <option value="Debit">Debit (Remove Money to Outside)</option>
                    <option value="Transfer">Transfer (Between Faisal & Waqas)</option>
                  </select>
                  {errors.type && touched.type && (
                    <p className="text-danger">{errors.type}</p>
                  )}
                </div>

                {/* Show different fields based on transaction type */}
                {values.type === "Transfer" ? (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">
                        Transfer From <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="from_person"
                        value={values.from_person}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <option value="">Select Person</option>
                        <option value="Faisal">Faisal</option>
                        <option value="Waqas">Sir Waqas</option>
                      </select>
                      {errors.from_person && touched.from_person && (
                        <p className="text-danger">{errors.from_person}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">
                        Transfer To <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="to_person"
                        value={values.to_person}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!values.from_person}
                      >
                        <option value="">Select Person</option>
                        {values.from_person !== "Faisal" && <option value="Faisal">Faisal</option>}
                        {values.from_person !== "Waqas" && <option value="Waqas">Sir Waqas</option>}
                      </select>
                      {errors.to_person && touched.to_person && (
                        <p className="text-danger">{errors.to_person}</p>
                      )}
                    </div>
                  </>
                ) : values.type ? (
                  <div className="col-md-6">
                    <label className="form-label">
                      {values.type === "Credit" ? "Credit To" : "Debit From"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="person"
                      value={values.person}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">Select Person</option>
                      <option value="Faisal">Faisal</option>
                      <option value="Waqas">Sir Waqas</option>
                    </select>
                    {errors.person && touched.person && (
                      <p className="text-danger">{errors.person}</p>
                    )}
                  </div>
                ) : null}

                <div className="col-md-6">
                  <label className="form-label">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="amount"
                    placeholder="Enter Amount"
                    min="0"
                    step="0.01"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errors.amount && touched.amount && (
                    <p className="text-danger">{errors.amount}</p>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    name="notes"
                    placeholder="Enter Notes"
                    value={values.notes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.notes && touched.notes && (
                    <p className="text-danger">{errors.notes}</p>
                  )}
                </div>

                {/* Updated Balances Display */}
                {values.type && values.amount && 
                 ((values.type === "Transfer" && values.from_person && values.to_person) || 
                  (values.type !== "Transfer" && values.person)) && (
                  <div className="col-md-12">
                    <div className="card bg-info text-white">
                      <div className="card-body">
                        <h6 className="mb-3">Updated Balances (After Transaction):</h6>
                        <div className="row">
                          <div className="col-md-4">
                            <strong>Faisal Balance:</strong>{" "}
                            <CurrencyFormat
                              value={updatedBalances.f_cash_balance}
                              displayType="text"
                              thousandSeparator
                              prefix="Rs "
                              suffix="/-"
                            />
                          </div>
                          <div className="col-md-4">
                            <strong>Sir Waqas Balance:</strong>{" "}
                            <CurrencyFormat
                              value={updatedBalances.w_cash_balance}
                              displayType="text"
                              thousandSeparator
                              prefix="Rs "
                              suffix="/-"
                            />
                          </div>
                          <div className="col-md-4">
                            <strong>Total Charity:</strong>{" "}
                            <CurrencyFormat
                              value={updatedBalances.total_charity}
                              displayType="text"
                              thousandSeparator
                              prefix="Rs "
                              suffix="/-"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-md-12">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Add Charity Transaction"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      {loading && (
        <div className="loader in">
          <div className="spinner-border main-spin"></div>
        </div>
      )}
    </div>
  );
}
